import React, { Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
import useViewController from '../../hooks/useViewController';
import ViewControllerContext from '../../contexts/ViewControllerContext';
import LoadingFallback from '../LoadingFallback/LoadingFallbackComponent';

import {
  AboutPageComponent,
  Callback,
  ErrorWrapper,
  HomeComponent,
  MainContentWrapperComponent,
  PrivacySectionComponent,
  ProfileLayout,
  ProfileViewComponent,
  SecuritySectionComponent,
  SkeletonComponent,
  ThemeEditor,
  ThemeSectionComponent,
  ThemeSettingsComponent,
  Guard,
} from '../../routes/routes';
import { appLoader } from '../../utils/appLoader';

const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path={'/'} element={<Outlet />}>
      {/**
       * Основная часть приложения
       */}
      <Route
        path="app"
        element={
          <SuspenseWrapper>
            <SkeletonComponent />
          </SuspenseWrapper>
        }
        errorElement={
          <SuspenseWrapper>
            <ErrorWrapper />
          </SuspenseWrapper>
        }
      >
        <Route
          id="app-loader"
          loader={appLoader}
          errorElement={
            <SuspenseWrapper>
              <ErrorWrapper />
            </SuspenseWrapper>
          }
        >
          <Route
            index
            element={
              <SuspenseWrapper>
                <HomeComponent />
              </SuspenseWrapper>
            }
          />

          <Route
            path="page"
            element={
              <SuspenseWrapper>
                <MainContentWrapperComponent />
              </SuspenseWrapper>
            }
          />

          <Route
            path="old_theme"
            element={
              <SuspenseWrapper>
                <ThemeSettingsComponent />
              </SuspenseWrapper>
            }
          />

          <Route
            path="theme"
            element={
              <SuspenseWrapper>
                <ThemeEditor />
              </SuspenseWrapper>
            }
          />

          <Route
            path="about"
            element={
              <Guard role="admin">
                <SuspenseWrapper>
                  <AboutPageComponent />
                </SuspenseWrapper>
              </Guard>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/app" replace />} />
      </Route>

      {/**
       * Профиль просматриваемого пользователя
       */}
      <Route
        path="profile"
        element={
          <SuspenseWrapper>
            <ProfileLayout />
          </SuspenseWrapper>
        }
      >
        <Route
          index
          element={
            <SuspenseWrapper>
              <ProfileViewComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="security"
          element={
            <SuspenseWrapper>
              <SecuritySectionComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="theme"
          element={
            <SuspenseWrapper>
              <ThemeSectionComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="privacy"
          element={
            <SuspenseWrapper>
              <PrivacySectionComponent />
            </SuspenseWrapper>
          }
        />
        <Route path="*" element={<Navigate to="/profile" replace />} />
      </Route>

      {/**
       * Авторизация
       */}
      <Route
        path="/callback"
        element={
          <SuspenseWrapper>
            <Callback />
          </SuspenseWrapper>
        }
      />

      {/**
       * Редиректы
       */}
      <Route path={'/'} element={<Navigate to={'/app'} replace={true} />} />
      <Route path={'*'} element={<Navigate to={'/app'} replace={true} />} />
    </Route>,
  ),
);

const RouterComponent = () => {
  const viewController = useViewController();

  return (
    <ViewControllerContext.Provider value={viewController}>
      <RouterProvider router={router} />
    </ViewControllerContext.Provider>
  );
};

export default RouterComponent;
